/**
 * Subscription Service - Handles all subscription-related API operations
 */
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import { getAuthHeader } from './authService';

const API_BASE = API_CONFIG.BASE_URL;

/**
 * Get all available subscription plans
 * @returns {Promise} Promise with subscription plans array
 */
export const getSubscriptionPlans = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_PLANS}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get subscription plans error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get subscription plan by ID
 * @param {string} planId - Plan ID
 * @returns {Promise} Promise with plan data
 */
export const getSubscriptionPlan = async (planId) => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_PLANS}/${planId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get subscription plan error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get current subscription for tenant
 * @returns {Promise} Promise with current subscription data
 */
export const getCurrentSubscription = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_CURRENT}`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get current subscription error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get subscription history for tenant
 * @param {Object} params - Query parameters (page, size, etc.)
 * @returns {Promise} Promise with subscription history
 */
export const getSubscriptionHistory = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_HISTORY}${queryParams ? `?${queryParams}` : ''}`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get subscription history error:', error);
    throw handleApiError(error);
  }
};

/**
 * Start trial subscription
 * @param {string} planId - Plan ID for trial
 * @returns {Promise} Promise with trial subscription data
 */
export const startTrial = async (planId) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_START_TRIAL}`,
      null,
      {
        params: { planId },
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Start trial error:', error);
    throw handleApiError(error);
  }
};

/**
 * Convert trial to paid subscription
 * @param {Object} conversionData - Trial conversion data
 * @returns {Promise} Promise with converted subscription data
 */
export const convertTrialToPaid = async (conversionData) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_CONVERT_TRIAL}`,
      conversionData,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Convert trial error:', error);
    throw handleApiError(error);
  }
};

/**
 * Change subscription plan (upgrade/downgrade)
 * @param {string} newPlanId - New plan ID
 * @param {boolean} prorated - Whether to apply proration
 * @returns {Promise} Promise with updated subscription data
 */
export const changeSubscriptionPlan = async (newPlanId, prorated = true) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_CHANGE_PLAN}`,
      null,
      {
        params: { newPlanId, prorated },
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Change subscription plan error:', error);
    throw handleApiError(error);
  }
};

/**
 * Cancel subscription
 * @param {string} reason - Cancellation reason
 * @returns {Promise} Promise with cancellation result
 */
export const cancelSubscription = async (reason) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_CANCEL}`,
      null,
      {
        params: { reason },
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Cancel subscription error:', error);
    throw handleApiError(error);
  }
};

/**
 * Extend trial period (admin only)
 * @param {number} additionalDays - Additional days to extend
 * @returns {Promise} Promise with extended trial data
 */
export const extendTrial = async (additionalDays) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_EXTEND_TRIAL}`,
      null,
      {
        params: { additionalDays },
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Extend trial error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get trial status for current tenant
 * @returns {Promise} Promise with trial status data
 */
export const getTrialStatus = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_TRIAL_STATUS}`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get trial status error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get subscription statistics (admin only)
 * @returns {Promise} Promise with subscription statistics
 */
export const getSubscriptionStatistics = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_STATISTICS}`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get subscription statistics error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get plan statistics (admin only)
 * @param {string} planId - Plan ID
 * @returns {Promise} Promise with plan statistics
 */
export const getPlanStatistics = async (planId) => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_PLANS}/${planId}/statistics`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get plan statistics error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get revenue metrics (admin only)
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise} Promise with revenue metrics
 */
export const getRevenueMetrics = async (startDate, endDate) => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_REVENUE_METRICS}`,
      {
        params: { startDate, endDate },
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get revenue metrics error:', error);
    throw handleApiError(error);
  }
};

/**
 * Create subscription plan (admin only)
 * @param {Object} planData - Plan data
 * @returns {Promise} Promise with created plan
 */
export const createSubscriptionPlan = async (planData) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_PLANS}`,
      planData,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Create subscription plan error:', error);
    throw handleApiError(error);
  }
};

/**
 * Update subscription plan (admin only)
 * @param {string} planId - Plan ID
 * @param {Object} planData - Updated plan data
 * @returns {Promise} Promise with updated plan
 */
export const updateSubscriptionPlan = async (planId, planData) => {
  try {
    const response = await axios.put(
      `${API_BASE}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_PLANS}/${planId}`,
      planData,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Update subscription plan error:', error);
    throw handleApiError(error);
  }
};

/**
 * Process expired trials (admin only)
 * @returns {Promise} Promise with processing result
 */
export const processExpiredTrials = async () => {
  try {
    const response = await axios.post(
      `${API_BASE}/subscriptions/process-expired-trials`,
      null,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Process expired trials error:', error);
    throw handleApiError(error);
  }
};

/**
 * Handle API errors consistently
 * @param {Error} error - Axios error object
 * @returns {Error} Formatted error
 */
const handleApiError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    const message = data?.message || data?.error || 'An error occurred';
    
    switch (status) {
      case 400:
        return new Error(`Invalid request: ${message}`);
      case 401:
        return new Error('Authentication required');
      case 403:
        return new Error('Access forbidden');
      case 404:
        return new Error('Resource not found');
      case 409:
        return new Error(`Conflict: ${message}`);
      case 500:
        return new Error('Server error. Please try again later.');
      default:
        return new Error(`Error (${status}): ${message}`);
    }
  } else if (error.request) {
    return new Error('Network error. Please check your connection.');
  } else {
    return new Error(`Request error: ${error.message}`);
  }
};

// Export all functions as default object
const subscriptionService = {
  getSubscriptionPlans,
  getSubscriptionPlan,
  getCurrentSubscription,
  getSubscriptionHistory,
  startTrial,
  convertTrialToPaid,
  changeSubscriptionPlan,
  cancelSubscription,
  extendTrial,
  getTrialStatus,
  getSubscriptionStatistics,
  getPlanStatistics,
  getRevenueMetrics,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  processExpiredTrials
};

export default subscriptionService;
